import React, { useState, useMemo } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import BookShelf from './BookShelf';

/**
 * ShelfView - CSS 3D bookshelf visualization of collection
 * 
 * Displays items as book spines on shelves with:
 * - CSS 3D perspective for depth
 * - Grouping by platform, category, year, or alphabetical
 * - Sorting options
 * - Responsive layout (3D on desktop, flat scroll on mobile)
 */
const ShelfView = ({ items, onItemClick, onViewModeChange, collectionType = 'games' }) => {
  const defaultGroupBy = collectionType === 'music' ? 'format'
    : collectionType === 'movies' ? 'format'
    : collectionType === 'lego' ? 'theme'
    : collectionType === 'consoles' ? 'manufacturer'
    : collectionType === 'books' ? 'genre'
    : collectionType === 'board-games' ? 'category'
    : 'platform';
  const [groupBy, setGroupBy] = useState(defaultGroupBy);
  const [sortBy, setSortBy] = useState('alpha_asc');

  // Platform colors for shelf labels
  const platformColors = {
    'PlayStation 5': '#003087',
    'PlayStation 4': '#003087',
    'PlayStation 3': '#003087',
    'PlayStation 2': '#003087',
    'PlayStation': '#003087',
    'Xbox Series X|S': '#107c10',
    'Xbox One': '#107c10',
    'Xbox 360': '#107c10',
    'Xbox': '#107c10',
    'Nintendo Switch': '#e60012',
    'Wii U': '#e60012',
    'Wii': '#e60012',
    'Nintendo 64': '#e60012',
    'PC (Microsoft Windows)': '#1a1a1a',
    'Default': '#8B4513'
  };

  // Group items based on selected grouping
  const groupedItems = useMemo(() => {
    if (!items || items.length === 0) return {};

    const groups = {};

    items.forEach(item => {
      let key = 'Other';

      switch (groupBy) {
        case 'platform':
          key = item.platform || 'Unknown Platform';
          break;
        case 'format':
          key = item.format || 'Unknown Format';
          break;
        case 'theme':
          key = item.themeName || item.theme || 'Unknown Theme';
          break;
        case 'manufacturer':
          key = item.manufacturer || item.brand || 'Unknown Manufacturer';
          break;
        case 'genre':
          key = item.genre || 'Unknown Genre';
          break;
        case 'author':
          key = item.author || 'Unknown Author';
          break;
        case 'series':
          key = item.series || 'Standalone';
          break;
        case 'category':
          if (collectionType === 'games') key = item.platform || 'Unknown';
          else key = item.category || 'Unknown Category';
          break;
        case 'year': {
          const year = item.year || item.releaseDate?.substring(0, 4) || 'Unknown Year';
          key = year;
          break;
        }
        case 'alpha': {
          const firstChar = (item.name || item.title || 'Z')[0].toUpperCase();
          key = /[A-Z]/.test(firstChar) ? firstChar : '#';
          break;
        }
        default:
          key = 'All Items';
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });

    // Sort items within each group
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => {
        const nameA = (a.name || a.title || '').toLowerCase();
        const nameB = (b.name || b.title || '').toLowerCase();
        
        switch (sortBy) {
          case 'alpha_asc':
            return nameA.localeCompare(nameB);
          case 'alpha_desc':
            return nameB.localeCompare(nameA);
          case 'year_asc':
            return (a.year || 9999) - (b.year || 9999);
          case 'year_desc':
            return (b.year || 0) - (a.year || 0);
          case 'recent':
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          default:
            return 0;
        }
      });
    });

    return groups;
  }, [items, groupBy, sortBy, collectionType]);

  // Sort group keys
  const sortedGroupKeys = useMemo(() => {
    const keys = Object.keys(groupedItems);
    
    if (groupBy === 'alpha') {
      return keys.sort((a, b) => {
        if (a === '#') return 1;
        if (b === '#') return -1;
        return a.localeCompare(b);
      });
    }
    
    if (groupBy === 'year') {
      return keys.sort((a, b) => {
        if (a === 'Unknown Year') return 1;
        if (b === 'Unknown Year') return -1;
        return parseInt(b) - parseInt(a); // Newest first
      });
    }
    
    return keys.sort();
  }, [groupedItems, groupBy]);

  if (!items || items.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No items in your collection yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Add items to see them displayed on your virtual shelf
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Controls bar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          marginBottom: 3,
          padding: 2,
          backgroundColor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1
        }}
      >
        {/* Group by selector */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Group by</InputLabel>
          <Select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            label="Group by"
          >
            {collectionType === 'music' && <MenuItem value="format">Format</MenuItem>}
            {collectionType === 'music' && <MenuItem value="year">Year</MenuItem>}
            {collectionType === 'music' && <MenuItem value="alpha">A-Z</MenuItem>}
            {collectionType === 'lego' && <MenuItem value="theme">Theme</MenuItem>}
            {collectionType === 'lego' && <MenuItem value="year">Year</MenuItem>}
            {collectionType === 'lego' && <MenuItem value="alpha">A-Z</MenuItem>}
            {collectionType === 'consoles' && <MenuItem value="manufacturer">Manufacturer</MenuItem>}
            {collectionType === 'consoles' && <MenuItem value="year">Year</MenuItem>}
            {collectionType === 'consoles' && <MenuItem value="alpha">A-Z</MenuItem>}
            {collectionType === 'games' && <MenuItem value="platform">Platform</MenuItem>}
            {collectionType === 'movies' && <MenuItem value="format">Format</MenuItem>}
            {(collectionType === 'games' || collectionType === 'movies') && <MenuItem value="year">Year</MenuItem>}
            {(collectionType === 'games' || collectionType === 'movies') && <MenuItem value="alpha">A-Z</MenuItem>}
            {collectionType === 'books' && <MenuItem value="genre">Genre</MenuItem>}
            {collectionType === 'books' && <MenuItem value="author">Author</MenuItem>}
            {collectionType === 'books' && <MenuItem value="series">Series</MenuItem>}
            {collectionType === 'books' && <MenuItem value="year">Year</MenuItem>}
            {collectionType === 'books' && <MenuItem value="alpha">A-Z</MenuItem>}
            {collectionType === 'board-games' && <MenuItem value="category">Category</MenuItem>}
            {collectionType === 'board-games' && <MenuItem value="year">Year</MenuItem>}
            {collectionType === 'board-games' && <MenuItem value="alpha">A-Z</MenuItem>}
          </Select>
        </FormControl>

        {/* Sort by selector */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            label="Sort by"
          >
            <MenuItem value="alpha_asc">A → Z</MenuItem>
            <MenuItem value="alpha_desc">Z → A</MenuItem>
            <MenuItem value="year_asc">Year (Old → New)</MenuItem>
            <MenuItem value="year_desc">Year (New → Old)</MenuItem>
            <MenuItem value="recent">Recently Added</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Bookcase room — dark warm background matching reference photography style */}
      <Box
        sx={{
          padding: { xs: 2, md: 3 },
          borderRadius: 2,
          minHeight: '400px',
          // Deep dark warm brown, almost black, with a subtle warm centre glow
          // matching the AI-render reference images provided by the user
          background: 'radial-gradient(ellipse at 50% 30%, #2e1508 0%, #110804 50%, #060302 100%)',
          boxShadow: 'inset 0 0 120px rgba(0,0,0,0.7)',
        }}
      >
        {/* Collection stats */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h5" gutterBottom>
            Your Collection
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {items.length} {items.length === 1 ? 'item' : 'items'} across {sortedGroupKeys.length} {sortedGroupKeys.length === 1 ? 'shelf' : 'shelves'}
          </Typography>
        </Box>

        {/* Shelves */}
        {sortedGroupKeys.map((groupKey) => (
          <BookShelf
            key={groupKey}
            label={groupKey}
            items={groupedItems[groupKey]}
            onItemClick={onItemClick}
            color={platformColors[groupKey] || platformColors['Default']}
            collectionType={collectionType}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ShelfView;
